import React, { useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Table from 'react-bootstrap/Table';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid,
    Tooltip as ChartTooltipPrimitive, Legend, ResponsiveContainer,
} from 'recharts';
import httpService from '../../../services/httpService';
import { TabSpinner, Th, Avatar, formatRelative } from './shared';

const SEGMENT_VARIANT = { Active: 'success', 'At Risk': 'warning', Lapsed: 'danger', 'No Activity': 'secondary' };

const GrowthTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-white border rounded px-3 py-2 shadow-sm" style={{ fontSize: 13 }}>
            <div className="fw-semibold mb-1">w/c {label}</div>
            <div style={{ color: '#5A9CB5' }}>+{payload.find((p) => p.dataKey === 'new_subscribers')?.value ?? 0} new</div>
            <div style={{ color: '#FACE68' }}>{payload.find((p) => p.dataKey === 'cumulative')?.value ?? 0} total</div>
        </div>
    );
};

const SubscribersTab = ({ clubGuid }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        httpService.get(`/dash/podcast-club/${clubGuid}/statistics/subscribers`)
            .then((res) => setData(res.data))
            .catch((err) => setError(err.response?.data?.message || 'Error loading data'))
            .finally(() => setLoading(false));
    }, [clubGuid]);

    if (loading) return <TabSpinner />;
    if (error) return <Alert variant="danger">{error}</Alert>;
    if (!data) return null;

    const growth = data.subscriber_growth || [];
    const retentionSummary = data.retention?.summary || [];
    const retentionListeners = data.retention?.listeners || [];
    const invite = data.invite_conversion || null;

    return (
        <div>
            {/* Subscriber growth */}
            <div className="mb-5">
                <h6 className="fw-semibold mb-1">Subscriber growth</h6>
                <p className="text-muted small mb-3">
                    Weekly new subscribers and running total. Use this to spot which weeks — and which episode releases — drove the most sign-ups.
                </p>
                {growth.length === 0 ? (
                    <p className="text-muted small">No subscriber data yet.</p>
                ) : (
                    <ResponsiveContainer width="100%" height={240}>
                        <LineChart data={growth} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                            <ChartTooltipPrimitive content={<GrowthTooltip />} />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Line type="monotone" dataKey="new_subscribers" name="New this week" stroke="#5A9CB5" strokeWidth={2} dot={{ r: 3 }} />
                            <Line type="monotone" dataKey="cumulative" name="Total subscribers" stroke="#FACE68" strokeWidth={2} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </div>

            {/* Listener retention */}
            <div className="mb-5">
                <h6 className="fw-semibold mb-1">Listener retention</h6>
                <p className="text-muted small mb-3">
                    Subscribers segmented by recency of their last practice or comment.{' '}
                    <strong>Active</strong> = activity in the last 30 days.{' '}
                    <strong>At Risk</strong> = 30–60 days ago.{' '}
                    <strong>Lapsed</strong> = over 60 days ago.
                </p>
                {retentionSummary.length > 0 && (
                    <div className="row g-3 mb-3">
                        {retentionSummary.map((seg, i) => (
                            <div key={i} className="col-3">
                                <div className="card border-0 stat-card px-3 py-2 text-center rounded-3 h-100">
                                    <div className="fw-bold fs-4">{seg.count}</div>
                                    <div className="small mt-1">
                                        <Badge bg={SEGMENT_VARIANT[seg.label] || 'secondary'} className="me-1">{seg.label}</Badge>
                                        <span className="text-muted">{seg.percent}%</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <hr/>
                {/* Invite conversion */}
                {invite && invite.total_sent > 0 && (
                    <div>
                        <h6 className="fw-semibold mb-1">Invite conversion</h6>
                        <p className="text-muted small mb-3">
                            Of the personal invites sent for this club, how many resulted in a sign-up?
                        </p>
                        <div className="row g-3">
                            {[
                                { label: 'Sent',            value: invite.total_sent },
                                { label: 'Accepted',        value: invite.accepted },
                                { label: 'Pending',         value: invite.pending },
                                { label: 'Expired',         value: invite.expired },
                                { label: 'Conversion rate', value: `${invite.conversion_rate}%` },
                            ].map((item, i) => (
                                <div key={i} className="col" style={{ flex: '0 0 20%', maxWidth: '20%' }}>
                                    <div className="card border-0 stat-card px-3 py-2 text-center rounded-3 h-100">
                                        <div className="fw-bold fs-4">{item.value}</div>
                                        <div className="text-muted small mt-1">{item.label}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                <hr/>
                {retentionListeners.length > 0 && (
                    <Table hover responsive size="sm" className="align-middle">
                        <thead className="table-light">
                            <tr>
                                <Th tip="The subscriber's name.">Listener</Th>
                                <Th tip="When they last recorded a practice or posted a comment in this club.">Last activity</Th>
                                <Th tip="Days since their last activity.">Days inactive</Th>
                                <Th tip="Engagement segment based on recency.">Status</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {retentionListeners.map((l, i) => (
                                <tr key={i}>
                                    <td>
                                        <div className="d-flex align-items-center gap-2">
                                            <Avatar guid={l.user_guid} />
                                            <span className="fw-medium">{l.listener_name}</span>
                                        </div>
                                    </td>
                                    <td className="text-muted">{l.last_activity ? formatRelative(l.last_activity) : '—'}</td>
                                    <td className="text-muted">{l.days_since !== null ? l.days_since : '—'}</td>
                                    <td><Badge bg={SEGMENT_VARIANT[l.segment] || 'secondary'}>{l.segment}</Badge></td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </div>
        </div>
    );
};

export default SubscribersTab;
